import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkProps } from "@waytek/MobiusOverrides/Link/Link";
import { completeProductUrl, isBulkProduct, productNotAvailable } from "@waytek/Utilities/global/products";
import { isAuthenticated, signInUrl } from "@waytek/Utilities/global/users";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const currentLocation = getLocation(state);
    const returnUrl = currentLocation.pathname + currentLocation.search;
    const forwardUrl = `${signInUrl(state)}?returnUrl=${encodeURIComponent(returnUrl)}`;
    return {
        isAuthenticated: isAuthenticated(state),
        forwardUrl,
    };
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;
export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    smallGridItem?: GridItemProps;
    smallGridItemHidden?: HiddenProps;
    largeGridItem?: GridItemProps;
    numbersWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    labelStyle?: InjectableCss;
    attributesStyle?: InjectableCss;
    pricingBox?: InjectableCss;
    siteMessageWrapper?: InjectableCss;
    siteMessage?: TypographyPresentationProps;
    siteMessageLink?: LinkProps;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<Props> = ({ id, product, productInfo, isAuthenticated: isAuth, forwardUrl }) => {
    const isBulk = isBulkProduct(product.productNumber);
    const { isDiscontinued, minimumOrderQty } = product;
    const { inventory } = productInfo;

    const pricingBox = (
        <StyledWrapper {...styles.pricingBox}>
            {!isAuth && isBulk && (
                <StyledWrapper {...styles.siteMessageWrapper}>
                    <Typography {...styles.siteMessage}>
                        {siteMessage("CSM_You_Must_Be_Logged_In")}{" "}
                        <Link data-test-selector="productDetails_signIn" href={forwardUrl} {...styles.siteMessageLink}>
                            {siteMessage("CSM_Log_in_here")}
                        </Link>
                    </Typography>
                </StyledWrapper>
            )}

            {!isBulk && product.isDiscontinued && (
                <>
                    <StyledWrapper {...styles.siteMessageWrapper} style={{ marginTop: 10 }}>
                        <Typography {...styles.siteMessage}>
                            {product.detail?.replacementProductId ? (
                                <>
                                    {siteMessage("CSM_Item_Discontinued_Replaced")}{" "}
                                    <Link
                                        data-test-selector="productDetails_discontinued"
                                        href={completeProductUrl(product.properties?.replacementProductUrl)}
                                        {...styles.siteMessageLink}
                                    >
                                        {product.properties?.replacementProductNumber}
                                    </Link>
                                </>
                            ) : (
                                siteMessage("CSM_Item_Discontinued")
                            )}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.siteMessageWrapper} style={{ marginTop: 10 }}></StyledWrapper>
                </>
            )}

            {(isAuth ||
                (!isAuth && !isBulk) ||
                (isDiscontinued && (inventory?.qtyOnHand ?? minimumOrderQty + 1) < minimumOrderQty)) && (
                <Zone contentId={id} zoneName="ContentPricing" />
            )}
        </StyledWrapper>
    );

    const docBox = (
        <StyledWrapper>
            <Zone contentId={id} zoneName="ContentDocuments" />
        </StyledWrapper>
    );

    const extraRightBox = (
        <StyledWrapper>
            <Zone contentId={id} zoneName="ContentRight" />
        </StyledWrapper>
    );

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                <GridContainer {...styles.leftColumnInnerContainer}>
                    <GridItem {...styles.largeGridItem}>
                        <Zone contentId={id} zoneName="ContentTitle" />
                    </GridItem>
                    <GridItem {...styles.largeGridItem}>
                        <StyledWrapper {...styles.numbersWrapper}>
                            <Zone contentId={id} zoneName="ContentNumbers" data-product-numbers={true} />
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.rightColumnGridItem} data-test-selector="productDetails_rightColumn_empty">
                <Hidden {...styles.rightColumnGridItemHidden}></Hidden>
            </GridItem>
            <GridItem {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                <GridContainer {...styles.leftColumnInnerContainer}>
                    <GridItem
                        {...styles.largeGridItem}
                        css={css`
                            order: 1;
                        `}
                    >
                        <StyledWrapper {...styles.imageWrapper}>
                            <Zone contentId={id} zoneName="ContentImage" />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem
                        {...styles.smallGridItem}
                        css={css`
                            order: 2;
                        `}
                    >
                        <Hidden {...styles.smallGridItemHidden}>{pricingBox}</Hidden>
                    </GridItem>
                    <GridItem {...styles.largeGridItem} css={styles.labelStyle?.css}>
                        <Zone contentId={id} zoneName="ContentLabels" />
                    </GridItem>
                    <GridItem {...styles.largeGridItem} css={styles.attributesStyle?.css}>
                        <Zone contentId={id} zoneName="ContentAttributes" />
                    </GridItem>
                    <GridItem
                        {...styles.smallGridItem}
                        css={css`
                            order: 5;
                        `}
                    >
                        <Hidden {...styles.smallGridItemHidden}>{docBox}</Hidden>
                    </GridItem>
                    <GridItem
                        {...styles.largeGridItem}
                        css={css`
                            order: 6;
                        `}
                    >
                        <Zone contentId={id} zoneName="ContentDescription" />
                    </GridItem>
                    <GridItem
                        {...styles.smallGridItem}
                        css={css`
                            order: 7;
                        `}
                    >
                        <Hidden {...styles.smallGridItemHidden}>{extraRightBox}</Hidden>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.rightColumnGridItem} data-test-selector="productDetails_rightColumn">
                <Hidden {...styles.rightColumnGridItemHidden}>
                    {pricingBox}
                    {docBox}
                    {extraRightBox}
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
